import React, { useEffect, useMemo, useRef, useState } from 'react';

import { ConfidenceBadge } from '../grounding/ConfidenceIndicator';
import { useRetrievalExperience } from '../../../hooks/useRetrievalExperience';

type ContextTab = 'sources' | 'timeline' | 'verification';

const relevanceColor = (score?: number): string => {
  if (typeof score !== 'number') {
    return 'bg-neutral-300';
  }
  if (score >= 0.7) {
    return 'bg-green-500';
  }
  if (score >= 0.45) {
    return 'bg-amber-500';
  }
  return 'bg-red-500';
};

const formatRuleName = (raw: string): string =>
  raw.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const entityTypeColor: Record<string, { bg: string; text: string; dot: string }> = {
  Project: { bg: 'bg-blue-100', text: 'text-blue-800', dot: 'bg-blue-500' },
  Person: { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500' },
  Methodology: { bg: 'bg-amber-100', text: 'text-amber-800', dot: 'bg-amber-500' },
  Industry: { bg: 'bg-indigo-100', text: 'text-indigo-800', dot: 'bg-indigo-500' },
  Document: { bg: 'bg-neutral-100', text: 'text-neutral-700', dot: 'bg-neutral-400' },
};

const getEntityTypeColor = (
  entityType: string,
): { bg: string; text: string; dot: string } =>
  entityTypeColor[entityType] || { bg: 'bg-purple-100', text: 'text-purple-800', dot: 'bg-purple-500' };

const ContextPanel: React.FC = () => {
  const { state, sortedSources, latestTimelineEntries, focusCitationRef } =
    useRetrievalExperience();
  const [activeTab, setActiveTab] = useState<ContextTab>('sources');
  const sourceContainerRef = useRef<HTMLDivElement | null>(null);

  const selectedNode = useMemo(() => {
    if (!state.selectedNodeId) {
      return null;
    }
    return state.graphNodes.find((node) => node.id === state.selectedNodeId) || null;
  }, [state.graphNodes, state.selectedNodeId]);

  const selectedNodeConnections = useMemo(() => {
    if (!selectedNode) {
      return [] as string[];
    }
    const connectedIds = state.graphEdges.reduce<string[]>((acc, edge) => {
      if (edge.source === selectedNode.id) {
        acc.push(edge.target);
      }
      if (edge.target === selectedNode.id) {
        acc.push(edge.source);
      }
      return acc;
    }, []);
    return connectedIds
      .map((id) => state.graphNodes.find((node) => node.id === id)?.data.label)
      .filter((value): value is string => Boolean(value));
  }, [selectedNode, state.graphEdges, state.graphNodes]);

  useEffect(() => {
    if (!state.focusedCitationRef || !sourceContainerRef.current) {
      return;
    }
    setActiveTab('sources');
    const selector = `[data-citation-ref="${CSS.escape(state.focusedCitationRef)}"]`;
    const target = sourceContainerRef.current.querySelector(selector) as HTMLElement | null;
    if (!target) {
      return;
    }
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.classList.add('ring-2', 'ring-blue-200');
    const timeout = window.setTimeout(() => {
      target.classList.remove('ring-2', 'ring-blue-200');
    }, 1400);
    return () => window.clearTimeout(timeout);
  }, [state.focusedCitationRef]);

  useEffect(() => {
    const handleScrollToSource = (rawEvent: Event) => {
      const customEvent = rawEvent as CustomEvent<{ citation_ref?: string }>;
      const citationRef = customEvent.detail?.citation_ref;
      if (!citationRef) {
        return;
      }
      focusCitationRef(citationRef);
    };

    window.addEventListener('curious:scroll-to-source', handleScrollToSource as EventListener);
    return () => {
      window.removeEventListener('curious:scroll-to-source', handleScrollToSource as EventListener);
    };
  }, [focusCitationRef]);

  useEffect(() => {
    const handleContextTab = (rawEvent: Event) => {
      const customEvent = rawEvent as CustomEvent<{ tab?: ContextTab }>;
      const tab = customEvent.detail?.tab;
      if (tab === 'sources' || tab === 'timeline' || tab === 'verification') {
        setActiveTab(tab);
      }
    };

    window.addEventListener('curious:context-tab', handleContextTab as EventListener);
    return () => {
      window.removeEventListener('curious:context-tab', handleContextTab as EventListener);
    };
  }, []);

  useEffect(() => {
    if (state.selectedNodeId) {
      setActiveTab('timeline');
    }
  }, [state.selectedNodeId]);

  const tabLabels: Record<ContextTab, string> = {
    sources: 'Sources',
    timeline: 'Details',
    verification: 'Verify',
  };

  return (
    <section className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
      <header className="border-b border-neutral-200 px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-neutral-900">Context</h3>
          {typeof state.confidenceScore === 'number' ? (
            <ConfidenceBadge confidence={state.confidenceScore} />
          ) : null}
        </div>
        <div className="mt-2 flex items-center gap-1.5 text-[11px]">
          {(['sources', 'timeline', 'verification'] as ContextTab[]).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`rounded-full px-2.5 py-1 font-semibold transition-all duration-150 ${
                activeTab === tab
                  ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm'
                  : 'bg-neutral-100 text-neutral-500 border border-transparent hover:bg-neutral-200 hover:text-neutral-700'
              }`}
            >
              {tabLabels[tab]}
            </button>
          ))}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto p-4" ref={sourceContainerRef}>
        {selectedNode ? (() => {
          const colors = getEntityTypeColor(selectedNode.data.entityType);
          return (
            <div className="mb-3 rounded-lg border border-neutral-200 bg-white p-3 shadow-sm">
              <div className="flex items-start gap-2">
                <span className={`mt-0.5 h-2 w-2 rounded-full shrink-0 ${colors.dot}`} />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-neutral-900 leading-tight">{selectedNode.data.label}</div>
                  <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${colors.bg} ${colors.text}`}>
                    {selectedNode.data.entityType}
                  </span>
                </div>
              </div>
              <p className="mt-1.5 text-[11px] text-neutral-500">
                Viewing details below
              </p>
            </div>
          );
        })() : null}

        {activeTab === 'sources' ? (
          sortedSources.length > 0 ? (
            <div className="space-y-2.5">
              {sortedSources.map((source, idx) => {
                const scorePercent = Math.round((source.relevanceScore || 0) * 100);
                const scoreColor = relevanceColor(source.relevanceScore);
                return (
                  <article
                    key={source.citationRef}
                    data-citation-ref={source.citationRef}
                    className="group rounded-xl border border-neutral-200 bg-white p-3 shadow-sm transition-all duration-150 hover:border-blue-200 hover:shadow-md"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-blue-100 text-[9px] font-bold text-blue-700 shrink-0">
                            {idx + 1}
                          </span>
                          <h4 className="truncate text-sm font-semibold text-neutral-900">
                            {source.sourceTitle}
                          </h4>
                        </div>
                        {source.citationRef ? (
                          <p className="mt-0.5 truncate font-mono text-[10px] text-neutral-400">
                            {source.citationRef}
                          </p>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        onClick={() => focusCitationRef(source.citationRef)}
                        className="shrink-0 rounded-md border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-blue-100"
                      >
                        Jump ↗
                      </button>
                    </div>
                    {source.sourceSnippet ? (
                      <p className="mt-2 line-clamp-3 text-xs leading-relaxed text-neutral-600">
                        {source.sourceSnippet}
                      </p>
                    ) : null}
                    {typeof source.relevanceScore === 'number' ? (
                      <div className="mt-2.5 flex items-center gap-2">
                        <div className="h-1 flex-1 overflow-hidden rounded-full bg-neutral-100">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${scoreColor}`}
                            style={{ width: `${Math.max(0, Math.min(100, scorePercent))}%` }}
                          />
                        </div>
                        <span className="text-[10px] font-semibold text-neutral-500">
                          {scorePercent}% relevance
                        </span>
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
              <svg className="w-8 h-8 text-neutral-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-sm font-medium text-neutral-400">No sources yet</p>
              <p className="text-xs text-neutral-400">Sources appear here once the assistant retrieves context for your query.</p>
            </div>
          )
        ) : null}

        {activeTab === 'timeline' ? (
          selectedNode ? (() => {
            const colors = getEntityTypeColor(selectedNode.data.entityType);
            const confidence = typeof selectedNode.data.confidence === 'number'
              ? selectedNode.data.confidence
              : null;
            return (
              <div className="space-y-3">
                {/* Entity header card */}
                <article className={`rounded-xl border p-4 ${colors.bg} border-transparent`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className={`text-[10px] font-semibold uppercase tracking-widest ${colors.text} opacity-70`}>
                        {selectedNode.data.entityType}
                      </p>
                      <p className="mt-1 text-base font-bold text-neutral-900 leading-tight">
                        {selectedNode.data.label}
                      </p>
                    </div>
                    {confidence !== null ? (
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <span className={`text-[10px] font-semibold ${colors.text}`}>
                          {Math.round(confidence * 100)}%
                        </span>
                        <div className="h-1.5 w-14 overflow-hidden rounded-full bg-white/60">
                          <div
                            className={`h-full rounded-full ${colors.dot}`}
                            style={{ width: `${Math.round(confidence * 100)}%` }}
                          />
                        </div>
                        <span className={`text-[9px] ${colors.text} opacity-60`}>confidence</span>
                      </div>
                    ) : null}
                  </div>
                </article>

                {/* Attributes */}
                {Object.keys(selectedNode.data.attributes || {}).length > 0 ? (
                  <article className="rounded-xl border border-neutral-200 bg-white p-3 shadow-sm">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-400 mb-2">
                      Properties
                    </p>
                    <dl className="space-y-2">
                      {Object.entries(selectedNode.data.attributes || {}).map(([key, value]) => (
                        <div key={key} className="flex items-start gap-3 text-xs">
                          <dt className="w-1/3 font-medium text-neutral-500 capitalize shrink-0">
                            {key.replace(/_/g, ' ')}
                          </dt>
                          <dd className="flex-1 text-neutral-800 break-words">{String(value)}</dd>
                        </div>
                      ))}
                    </dl>
                  </article>
                ) : null}

                {/* Connections */}
                <article className="rounded-xl border border-neutral-200 bg-white p-3 shadow-sm">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-400 mb-2">
                    Connected Nodes
                    {selectedNodeConnections.length > 0 ? (
                      <span className="ml-1.5 rounded-full bg-neutral-100 px-1.5 py-0.5 text-[9px] font-bold text-neutral-500">
                        {selectedNodeConnections.length}
                      </span>
                    ) : null}
                  </p>
                  {selectedNodeConnections.length > 0 ? (
                    <ul className="space-y-1">
                      {selectedNodeConnections.map((label, index) => (
                        <li
                          key={`${label}-${index}`}
                          className="flex items-center gap-2 text-xs text-neutral-700"
                        >
                          <span className="h-1.5 w-1.5 rounded-full bg-neutral-300 shrink-0" />
                          {label}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-neutral-400">No linked nodes in current view.</p>
                  )}
                </article>
              </div>
            );
          })() : latestTimelineEntries.length > 0 ? (
            <ol className="space-y-2">
              {latestTimelineEntries
                .slice()
                .reverse()
                .slice(0, 8)
                .map((entry) => (
                  <li
                    key={entry.id}
                    className="rounded-lg border border-neutral-200 bg-white p-2.5"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium text-neutral-800">
                        {entry.eventType}
                      </span>
                      <span className="text-[10px] text-neutral-500">
                        {new Date(entry.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-neutral-600">{entry.message}</p>
                  </li>
                ))}
            </ol>
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
              <svg className="w-8 h-8 text-neutral-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="3" strokeWidth="2" />
                <path strokeWidth="2" strokeLinecap="round" d="M12 3v2m0 14v2M3 12h2m14 0h2M5.64 5.64l1.41 1.41m9.9 9.9l1.41 1.41M5.64 18.36l1.41-1.41m9.9-9.9l1.41-1.41" />
              </svg>
              <p className="text-sm font-medium text-neutral-400">No node selected</p>
              <p className="text-xs text-neutral-400 max-w-[200px]">
                Click any node in the graph to inspect its details here.
              </p>
            </div>
          )
        ) : null}

        {activeTab === 'verification' ? (
          state.verification ? (
            <div className="space-y-3">
              <div
                className={`rounded-lg border p-3 ${
                  state.verification.passed
                    ? 'border-green-200 bg-green-50'
                    : 'border-amber-200 bg-amber-50'
                }`}
              >
                <p className="text-sm font-semibold text-neutral-900">
                  {state.verification.summary || 'Verification complete'}
                </p>
              </div>
              <div className="space-y-2">
                {state.verification.checks.map((check, index) => (
                  <div
                    key={`${check.ruleName}-${index}`}
                    className="rounded-lg border border-neutral-200 bg-white p-2.5"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium text-neutral-800">
                        {formatRuleName(check.ruleName)}
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                          !check.passed
                            ? check.severity === 'error'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-amber-100 text-amber-700'
                            : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {check.passed ? 'passed' : check.severity}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-neutral-600">{check.message}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-neutral-500">
              Verification results will appear after generation.
            </p>
          )
        ) : null}
      </div>
    </section>
  );
};

export default ContextPanel;
